<template>
  <div class="container mt-4">
    <h1>🔢 NumberField</h1>
    <p>
      A reusable Vue 3 numeric input component with built-in validation,
      labeling, and seamless <code>v-model</code> support.
    </p>

    <hr />

    <h2>✨ Features</h2>
    <ul>
      <li>🔄 Two-way binding via <code>v-model</code></li>
      <li>🔢 Automatically converts input to Number or null</li>
      <li>📏 Supports <code>min</code>, <code>max</code>, and <code>step</code></li>
      <li>⚠️ Built-in error display</li>
      <li>🏷 Optional label and required indicator</li>
      <li>🆔 Unique ID for accessibility</li>
    </ul>

    <hr />

    <h2>📦 Props</h2>
    <ul>
      <li><b>modelValue</b> (Number | null) — Bound value</li>
      <li><b>label</b> (String) — Input label</li>
      <li><b>min</b> (Number) — Minimum value</li>
      <li><b>max</b> (Number) — Maximum value</li>
      <li><b>step</b> (Number, default: 1) — Step increment</li>
      <li><b>error</b> (String) — Error message</li>
      <li><b>required</b> (Boolean) — Marks field as required</li>
    </ul>

    <hr />

    <h2>📡 Emits</h2>
    <ul>
      <li><b>update:modelValue</b> — Emits Number or null when value changes</li>
    </ul>

    <hr />

    <h2>📌 Live Example: Basic</h2>

    <div style="max-width: 300px;">
      <number-field v-model="basicValue" label="Age" />
      <p>Value: {{ basicValue }}</p>
    </div>

    <pre class="p-3 mb-2 bg-white text-dark"><code>
      &lt;number-field v-model="basicValue" label="Age" /&gt;

      data() {
        return {
          basicValue: null
        }
      }
    </code></pre>

    <hr />

    <h2>📌 Live Example: Validation</h2>

    <div style="max-width: 300px;">
      <number-field
          v-model="price"
          label="Price"
          :min="0"
          :error="priceError"
          required
      />

      <p>Value: {{ price }}</p>
    </div>

    <pre class="p-3 mb-2 bg-white text-dark"><code>
      &lt;number-field
        v-model="price"
        label="Price"
        :min="0"
        :error="priceError"
        required
      /&gt;

      data() {
        return {
          price: null
        }
      },
      computed: {
        priceError() {
          if (this.price === null) return 'Price is required'
          if (this.price &lt; 0) return 'Must be positive'
          return ''
        }
      }
    </code></pre>

    <hr />

    <h2>📌 Live Example: Constraints</h2>

    <div style="max-width: 300px;">
      <number-field
          v-model="quantity"
          label="Quantity"
          :min="1"
          :max="10"
          :step="1"
      />

      <p>Value: {{ quantity }}</p>
    </div>

    <pre class="p-3 mb-2 bg-white text-dark"><code>
      &lt;number-field
        v-model="quantity"
        label="Quantity"
        :min="1"
        :max="10"
        :step="1"
      /&gt;

      data() {
        return {
          quantity: 1
        }
      }
    </code></pre>

    <hr />

    <h2>🧠 Behavior</h2>
    <ul>
      <li>Empty input → returns <code>null</code></li>
      <li>Filled input → returns <code>Number</code></li>
      <li>Automatically converts string input to number</li>
    </ul>

    <hr />

    <h2>✅ Notes</h2>
    <ul>
      <li>Validation logic should be handled outside the component</li>
      <li><code>required</code> is only for UI indication</li>
      <li>Uses computed setter for value normalization</li>
    </ul>
  </div>
</template>

<script>
import NumberField from "@/components/common/number-field/NumberField.vue";
import { useVuelidate } from "@vuelidate/core";
import { required } from "@vuelidate/validators";

export default {
  components: {
    NumberField
  },

  setup: () => ({ v$: useVuelidate() }),
  data() {
    return {
      basicValue: null,
      price: null,
      quantity: 1
    };
  },

  validations () {
    return {
      form: {
        price: { required },
      }
    }
  },

  computed: {
    priceError() {
      if (this.price === null) return "Price is required";
      if (this.price < 0) return "Must be positive";
      return "";
    }
  }
};
</script>